import { VisiblityElement } from '@/components/snippets/visibility/visibility'

/**
 * MODAL CUSTOM ELEMENT
 * Handled as a custom element due to their being next to no state to manage, and due to how neatly it interacts with Tailwind
 *
 * For set up:
 *   1. Ensure you wrap your modal elements with <x-modal>.
 *   2. Pass a 'data-modal-id' if you want intend to nest modals.
 *   3. Provide any buttons you want to modify modal state as a child, using "data-modal-role" with open, close, or toggle.
 *   4. To show or hide elements based on modal state, set a tailwind group and use "group-data-visible:" for stateful styling.
 *   5. Optional: Add 'data-toggle-group="true"' to automatically close other modals with the same attribute when this modal opens.
 *
 */

class ModalElement extends VisiblityElement {
  // Child role selectors
  static OPEN_SELECTOR = '[data-modal-role~="open"]'
  static CLOSE_SELECTOR = '[data-modal-role~="close"]'
  static TOGGLE_SELECTOR = '[data-modal-role~="toggle"]'

  parseIdSelector() {
    const id = this.dataset.id
    return id ? `[data-modal-parent~="${id}"]` : ''
  }

  /**
   * Controls are handled by delegation rather than bound one by one at upgrade time. The mini
   * cart's header — its close button included — is rendered by Preact well after
   * connectedCallback, so a control that did not exist at upgrade was silently never wired.
   *
   * The selector is the same one the old per-child binding used, so which elements count as this
   * modal's controls is unchanged; only when they are resolved has moved.
   *
   * Assigned in the constructor, like VisiblityElement's handlers, so add/remove always see the
   * same reference — `x-size-guide` extends this and does not call super.connectedCallback().
   */
  constructor() {
    super()

    const originalHandleShow = this.handleShow
    this.handleShow = () => {
      originalHandleShow.call(this)
      if (this.dataset.toggleGroup === 'true') this.closeOtherModalsInGroup()
    }

    this.handleRoleClick = (event) => {
      const control = event.target.closest(`${this.parseIdSelector()}[data-modal-role]`)
      if (!control || !this.contains(control)) return

      const roles = control.dataset.modalRole.split(/\s+/)
      if (roles.includes('open')) this.handleShow()
      else if (roles.includes('close')) this.handleHide()
      else if (roles.includes('toggle')) this.handleToggle()
    }
  }

  closeOtherModalsInGroup() {
    const parentContainer = this.parentElement || document
    parentContainer.querySelectorAll('x-modal[data-toggle-group="true"]').forEach((modal) => {
      if (modal !== this && modal.dataset.ui === 'visible') modal.handleHide()
    })
  }

  addEventListeners() {
    super.addEventListeners()
    this.addEventListener('click', this.handleRoleClick)
  }

  removeEventListeners() {
    super.removeEventListeners()
    this.removeEventListener('click', this.handleRoleClick)
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListeners()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }
}

customElements.define('x-modal', ModalElement)
